import { sugarDateTime } from '@app/lib'
import {
  type SugarPaymentSetupAch,
  type SugarPaymentSetupConfirmable,
  type SugarPaymentSetupW9,
  SugarPaymentSetupType,
  type SugarPaymentSetupViewResponse
} from '@app/types'
import { Stack, Text } from '@mantine/core'
import { type UseQueryResult } from '@tanstack/react-query'
import { compact, join } from 'lodash'
import { type ReactNode } from 'react'

export interface PaymentSetupConfig<T extends SugarPaymentSetupConfirmable> {
  attestationLabel: string
  emptyCta: string
  emptyDescription: string
  emptyTitle: string
  paymentSetupType: SugarPaymentSetupType
  title: string
  toItems: (record: T) => Array<{ label: string; value: ReactNode } | null>
}

// Two separately-typed consts, NOT a Record keyed on SugarPaymentSetupType: a Record widens to a
// union at the call site, which makes toItems uncallable under strict mode.
export const achPaymentSetupConfig: PaymentSetupConfig<SugarPaymentSetupAch> = {
  attestationLabel:
    'I certify that the bank account information shown above is true and correct to the best of my knowledge.',
  emptyCta: 'Set up direct deposit',
  emptyDescription: "We can't send your Universal Preschool payments until a bank account is on file.",
  emptyTitle: 'No bank account on file',
  paymentSetupType: SugarPaymentSetupType.ACH,
  title: 'Direct Deposit (ACH)',
  toItems: (ach) => [
    {
      label: 'Account holder',
      value: ach.accountName
    },
    {
      label: 'Bank',
      value: ach.bankName
    },
    {
      label: 'Account type',
      value: ach.accountTypeDisplay
    },
    {
      label: 'Account number',
      value: ach.accountNumberMasked
    },
    {
      label: 'Routing number',
      value: ach.routingNumberMasked
    }
  ]
}

export const w9PaymentSetupConfig: PaymentSetupConfig<SugarPaymentSetupW9> = {
  attestationLabel: 'I certify that the tax information shown above is true and correct to the best of my knowledge.',
  emptyCta: 'Set up tax information',
  emptyDescription: "We can't issue your Universal Preschool payments until a completed W-9 is on file.",
  emptyTitle: 'No W-9 on file',
  paymentSetupType: SugarPaymentSetupType.W9,
  title: 'Tax Information (W-9)',
  toItems: (w9) => [
    {
      label: 'Tax name',
      value: w9.taxName
    },
    {
      label: 'TIN type',
      value: w9.tinTypeDisplay
    },
    {
      label: 'TIN',
      value: w9.tinMasked
    },
    {
      label: 'Tax classification',
      value: w9.taxClassificationDisplay
    },
    // llcClassification is non-null only when taxClassification is 'llc'; PropertyList's
    // compact() drops the whole row rather than rendering a dash for everyone else
    w9.llcClassificationDisplay
      ? {
          label: 'LLC category',
          value: w9.llcClassificationDisplay
        }
      : null,
    {
      label: 'Tax address',
      // taxAddress is always an object with all four keys when the record exists — values may be
      // null but the object never is. The optional chaining below is belt-and-braces: the page has
      // no error boundary, so a broken contract here would otherwise take down the whole page.
      value: (
        <Stack spacing={0}>
          <Text size='sm'>{w9.taxAddress?.street || '-'}</Text>
          <Text size='sm'>
            {join(
              compact([w9.taxAddress?.city, join(compact([w9.taxAddress?.state, w9.taxAddress?.postalCode]), ' ')]),
              ', '
            ) || '-'}
          </Text>
        </Stack>
      )
    }
  ]
}

// The ONLY place rows are assembled. Both the panel and the confirm modal call this, so the two
// renderings cannot drift — drift is the one path by which this redesign could surface a field the
// masked-only list never intended.
export const toPaymentSetupItems = <T extends SugarPaymentSetupConfirmable>(config: PaymentSetupConfig<T>, record: T) =>
  compact([
    ...config.toItems(record),
    {
      label: 'Status',
      value: record.statusDisplay
    },
    {
      label: 'Confirmed',
      value: record.confirmedDate
        ? join(compact([sugarDateTime(record.confirmedDate), record.confirmedByName]), ' by ')
        : 'Not yet confirmed'
    }
  ])

// Affirmatively-known-empty ONLY. Deliberately false for both error branches: they mean "we could not
// find out", and badging them would tell a provider who HAS submitted bank details that they have not.
// Same reasoning for a pending first submission: nothing is approved yet, but telling them we still need
// it is what makes them submit again.
export const isPaymentSetupEmpty = (query: UseQueryResult<SugarPaymentSetupViewResponse<any>>) =>
  query.isSuccess && query.data?.success === true && !query.data.data && !query.data.pendingSubmission
